import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { Observable } from 'rxjs';
import { environment } from '@environments/environment';
import {
  UserSubscriptionDetails,
  UserSubscriptionUsages
} from '@pages/assistant/subscription/subscription.interface';
import { Session } from '@providers/session/session.service';
import { GetUserSubscriptionParams } from './subscription.request.interface';

/**
 * Service for connecting with chat API.
 */
@Injectable()
export class SubscriptionService {
  private readonly http = inject(HttpClient);
  private readonly session = inject(Session);

  /**
   * Get HTTP Headers.
   *
   * @returns {{ Authorization: string } | undefined }
   */
  getHttpHeaders(): { Authorization: string } | undefined {
    if (!this.session.token) {
      return undefined;
    }

    return {
      Authorization: `Bearer ${this.session.token}`
    };
  }

  /**
   * Get user subscription.
   *
   * @param {GetUserSubscriptionParams} params GetUserSubscriptionParams.
   * @returns {Observable<{ message: string; data: UserSubscriptionDetails; }>}
   */
  getUserSubscriptionDetails(params: GetUserSubscriptionParams): Observable<{
    message: string;
    data: UserSubscriptionDetails;
  }> {
    return this.http.get<{
      message: string;
      data: UserSubscriptionDetails;
    }>(
      `${environment.API_V2_URL}/subscription/user-subscriptions/get-by-user`,
      {
        headers: this.getHttpHeaders(),
        params: {
          ...params,
          onlyGetActivePeriod: true
        }
      }
    );
  }

  /**
   * Get user permission usages.
   *
   * @param {{ includePermissionDetail: boolean }} params GetUserSubscriptionParams.
   * @param {boolean} params.includePermissionDetail Include access permission detail.
   * @returns {Observable<{ message: string; data: { count: number; results: Array<UserSubscriptionUsages>; }; }>}
   */
  getUserSubscriptionUsages(params: {
    includePermissionDetail: boolean;
  }): Observable<{
    message: string;
    data: {
      count: number;
      results: Array<UserSubscriptionUsages>;
    };
  }> {
    return this.http.get<{
      message: string;
      data: {
        count: number;
        results: Array<UserSubscriptionUsages>;
      };
    }>(
      `${environment.API_V2_URL}/subscription/user-permission-usages/get-by-user`,
      {
        headers: this.getHttpHeaders(),
        params: {
          ...params,
          onlyGetActivePeriod: true
        }
      }
    );
  }
}
